/**
 * Runtime guard for the date picker wrappers.
 *
 * The design system picker props are typed as `Date | undefined`, but values
 * constructed elsewhere (e.g. `new Date(apiString)`) can be an Invalid Date, or
 * the wrong type entirely once data crosses a runtime boundary (API/JSON). Rather
 * than render a broken picker, we fail fast with a descriptive error that names
 * the offending prop.
 */

const describeValue = (value: unknown): string => {
    if (value instanceof Date) {
        return 'an Invalid Date';
    }
    return `${typeof value} (${String(value)})`;
};

// A value is acceptable when it is absent (undefined/null) or a valid Date.
// Anything else (a string, a number, or an Invalid Date) is rejected.
const isInvalidDateProp = (value: unknown): boolean =>
    value !== undefined && value !== null && (!(value instanceof Date) || Number.isNaN(value.getTime()));

/**
 * Throws if any of the supplied entries is present but not a valid Date.
 *
 * @param component - Wrapper name used in the error message (e.g. "DatePicker").
 * @param dates - Map of prop label to value to check (undefined/null pass).
 */
export const assertValidDateProps = (component: string, dates: Record<string, unknown>): void => {
    Object.entries(dates).forEach(([name, value]) => {
        if (isInvalidDateProp(value)) {
            throw new Error(
                `${component}: \`${name}\` must be a valid Date or undefined, but received ${describeValue(value)}.`,
            );
        }
    });
};
